import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useEntitlements } from "../hooks/useEntitlements";


import "../styles/generalStyle.css";
import "./CreateSongIdeas.css"; // ✅ Reutilizamos el mismo CSS

import { useLanguage } from "../i18n/LanguageContext";

export default function CreateSongWithLyrics() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const {
    loading: entLoading,
    canSong,
    consumeSongCredit,
    entitlements
  } = useEntitlements(firebaseUser?.uid);

  // Form
  const [titulo, setTitulo] = useState("");
  const [letra, setLetra] = useState("");
  const [estilo, setEstilo] = useState("");
  const [instrumentos, setInstrumentos] = useState("");
  const [voz, setVoz] = useState("hombre"); // ✅ default hombre

  // UI
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [createdId, setCreatedId] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  const letraLen = letra.trim().length;

  const prompt = useMemo(() => {
    const parts = [
      `${t("createSongWithLyrics.promptStyle")}: ${estilo?.trim() || "-"}`,
      instrumentos?.trim()
        ? `${t("createSongWithLyrics.promptInstruments")}: ${instrumentos.trim()}`
        : null,
      `${t("createSongWithLyrics.promptVoice")}: ${
        voz === "mujer" ? t("createSongWithLyrics.voiceFemale") : t("createSongWithLyrics.voiceMale")
      }`
    ].filter(Boolean);

    return parts.join(" | ");
  }, [t, estilo, instrumentos, voz]);

  const validate = () => {
    if (!firebaseUser) return t("createSongWithLyrics.errors.notLogged");
    if (!titulo.trim()) return t("createSongWithLyrics.titleRequiredError");
    if (!letra.trim()) return t("createSongWithLyrics.lyricsRequiredError");
    if (!estilo.trim()) return t("createSongWithLyrics.styleRequiredError");
    // instrumentos opcional
    // voz ya tiene default
    return "";
  };

  const handlePasteLyrics = async () => {
    try {
      setError("");
      // Pega desde portapapeles (solo funciona si el sitio tiene https y el user da permiso)
      if (!navigator.clipboard?.readText) {
        setError(t("createSongWithLyrics.errors.clipboardNotAvailable"));
        return;
      }
      const text = await navigator.clipboard.readText();
      if (!text?.trim()) {
        setError(t("createSongWithLyrics.errors.clipboardEmpty"));
        return;
      }
      setLetra(text);
    } catch (e) {
      console.error("Clipboard error:", e);
      setError(t("createSongWithLyrics.errors.clipboardDenied"));
    }
  };

  const handleCreate = async () => {
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    if (!canSong) {
      setError(t("limites.limiteAlcanzadoCancion"));
      return;
    }

    try {
      setSaving(true);
      setError("");
      setCreatedId(null);

      await consumeSongCredit();

      const cancionesCol = collection(db, "usuarios", firebaseUser.uid, "canciones");
      const docRef = doc(cancionesCol);

      const data = {
        id: docRef.id,
        userId: firebaseUser.uid,

        titulo: titulo.trim(),
        letra: letra,

        estilo: estilo.trim(),
        instrumentos: instrumentos.trim() ? instrumentos.trim() : null,
        voz: voz,

        prompt: prompt,

        pago: false,
        status: "pending",
        fechaCreacion: serverTimestamp()
      };

      await setDoc(docRef, data, { merge: true });

        // ✅ Crear/actualizar doc mini para compartir (cancionesWeb/{songId})
      const miniRef = doc(db, "cancionesWeb", docRef.id);

      const miniData = {
        songId: docRef.id,
        usuarioId: firebaseUser.uid,
        shareLang: "", 

        coverUrl: "",
        coverUrl02: "",
        videoUrl: "",
        videoUrl02: "",
        desdeWeb: true,

        status: "pending",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(miniRef, miniData, { merge: true });

      setCreatedId(docRef.id);
    } catch (e) {
      console.error("Error creando canción con letra:", e);
     // ✅ si falló por créditos
      if (String(e?.message || "").includes("NO_SONG_CREDITS")) {
        setError(t("limites.limiteAlcanzadoCancion"));
      } else {
        setError(t("createSong.errors.generic"));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!firebaseUser) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("createSongWithLyrics.notLoggedTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("createSongWithLyrics.notLoggedText")}
            </p>
            <button
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => navigate("/login")}
            >
              {t("createSongWithLyrics.goToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (createdId) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("createSongWithLyrics.successTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 10 }}>
              {t("createSongWithLyrics.successText")}
            </p>

            <div className="createdBox">
              <div className="createdRow">
                <span className="createdLabel">{t("createSongWithLyrics.statusLabel")}</span>
                <span className="createdValue">pending</span>
              </div>
              <div className="createdRow">
                <span className="createdLabel">{t("createSongWithLyrics.idLabel")}</span>
                <span className="createdValue">{createdId}</span>
              </div>
            </div>

            <button
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => navigate("/app/songs")}
            >
              {t("createSongWithLyrics.goToSongs")}
            </button>

            <button
              className="btn"
              style={{ marginTop: 10 }}
              onClick={() => {
                setCreatedId(null);
                setError("");
                setTitulo("");
                setLetra("");
                setEstilo("");
                setInstrumentos("");
                setVoz("hombre");
              }}
            >
              {t("createSongWithLyrics.createAnother")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="grid-2">
          {/* FORM */}
          <section className="card">
            <h1 className="h1">{t("createSongWithLyrics.title")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("createSongWithLyrics.subtitle")}
            </p>

            {/* Hint: ir a crear letra */}
            <div className="hintBox" style={{ marginTop: 10 }}>
              <p className="hintText">{t("createSongWithLyrics.hintText")}</p>
              <Link className="hintLink" to="/app/crear-letra">
                {t("createSongWithLyrics.goToCreateLyrics")}
              </Link>
            </div>

            <div className="form" style={{ marginTop: 12 }}>
              <label className="label">
                {t("createSongWithLyrics.songTitleLabel")} *
                <input
                  className="input"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder={t("createSongWithLyrics.songTitlePlaceholder")}
                  maxLength={80}
                />
              </label>

              <label className="label">
                {t("createSongWithLyrics.lyricsLabel")} *
                <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ width: "auto" }}
                    onClick={handlePasteLyrics}
                  >
                    {t("createSongWithLyrics.pasteLyrics")}
                  </button>
                  <span className="help-text">{t("createSongWithLyrics.pasteHelp")}</span>
                </div>

                <textarea
                  className="input textarea"
                  value={letra}
                  onChange={(e) => setLetra(e.target.value)}
                  placeholder={t("createSongWithLyrics.lyricsPlaceholder")}
                  rows={10}
                />

                <span className="help-text">
                  {t("createSongWithLyrics.lyricsCounter", { count: letraLen })}
                </span>
              </label>

              <label className="label">
                {t("createSongWithLyrics.styleLabel")} *
                <input
                  className="input"
                  value={estilo}
                  onChange={(e) => setEstilo(e.target.value)}
                  placeholder={t("createSongWithLyrics.stylePlaceholder")}
                  maxLength={80}
                />
              </label>

              <label className="label">
                {t("createSongWithLyrics.instrumentsLabel")}
                <input
                  className="input"
                  value={instrumentos}
                  onChange={(e) => setInstrumentos(e.target.value)}
                  placeholder={t("createSongWithLyrics.instrumentsPlaceholder")}
                  maxLength={120}
                />
                <span className="help-text">{t("createSongWithLyrics.instrumentsHelp")}</span>
              </label>

              <div className="label">
                {t("createSongWithLyrics.voiceLabel")}
                <div className="voiceRow">
                  <button
                    type="button"
                    className={`voiceChip ${voz === "hombre" ? "active" : ""}`}
                    onClick={() => setVoz("hombre")}
                  >
                    {t("createSongWithLyrics.voiceMale")}
                  </button>
                  <button
                    type="button"
                    className={`voiceChip ${voz === "mujer" ? "active" : ""}`}
                    onClick={() => setVoz("mujer")}
                  >
                    {t("createSongWithLyrics.voiceFemale")}
                  </button>
                </div>
                <span className="help-text">{t("createSongWithLyrics.voiceHelp")}</span>
              </div>

              {error ? <div className="alert-error">{error}</div> : null}

              <button
                type="button"
                className="btn btn-primary"
                disabled={saving || entLoading || !canSong}
                onClick={handleCreate}
              >
                {saving ? t("createSongWithLyrics.creatingButton") : t("createSongWithLyrics.createButton")}
              </button>

               {!entLoading && !canSong ? (
                <div className="alert-error" style={{ marginTop: 10 }}>
                  {t("limites.limiteAlcanzadoCancion")}
                 
                </div>
              ) : null}

              <p className="note">{t("createSongWithLyrics.note")}</p>
            </div>
          </section>

          {/* SUMMARY */}
          <section className="card">
            <h2 className="h2">{t("createSongWithLyrics.summaryTitle")}</h2>
            <p className="p-muted">{t("createSongWithLyrics.summarySubtitle")}</p>

            <div className="summaryBox">
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSongWithLyrics.summaryTitleLabel")}</span>
                <span className="summaryValue">{titulo.trim() || "--"}</span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSongWithLyrics.summaryStyle")}</span>
                <span className="summaryValue">{estilo.trim() || "--"}</span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSongWithLyrics.summaryInstruments")}</span>
                <span className="summaryValue">{instrumentos.trim() || "--"}</span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSongWithLyrics.summaryVoice")}</span>
                <span className="summaryValue">
                  {voz === "mujer" ? t("createSongWithLyrics.voiceFemale") : t("createSongWithLyrics.voiceMale")}
                </span>
              </div>
              <div className="summaryRow">
                <span className="summaryLabel">{t("createSongWithLyrics.summaryLyrics")}</span>
                <span className="summaryValue">{letra.trim() ? "✅" : "--"}</span>
              </div>
            </div>

            <div className="summaryPrompt">
              <span className="summaryLabel">{t("createSongWithLyrics.summaryPrompt")}</span>
              <div className="promptBox">{prompt}</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
